/**
 * Minimal Google Fonts Optimizer
 * Safely optimizes Google Fonts loading without breaking existing functionality
 * This approach adds enhancements rather than replacing existing code
 */

(function() {
  'use strict';
  
  // Configuration - very conservative settings
  const CONFIG = {
    onlyEnhanceExisting: true,
    respectOriginalBehavior: true,
    addFontDisplay: true,
    addPreconnectHints: true,
    monitorPerformance: true
  };

  class MinimalFontOptimizer {
    constructor() {
      this.changes = [];
      this.hasOptimized = false;
    }

    /**
     * Add font-display=swap to Google Fonts URLs that don't have it
     */
    enhanceGoogleFontsUrls() {
      // Find Google Fonts links
      const googleFontLinks = document.querySelectorAll('link[href*="fonts.googleapis.com"]');
      
      googleFontLinks.forEach(link => {
        const originalHref = link.href;
        
        // Only modify if display parameter is missing
        if (!originalHref.includes('display=')) {
          const separator = originalHref.includes('?') ? '&' : '?';
          const newHref = `${originalHref}${separator}display=swap`;
          
          // Update the href
          link.href = newHref;
          this.changes.push(`Enhanced Google Font URL: ${originalHref} → added display=swap`);
        }
      });
    }

    /**
     * Check preconnect hints for Google Fonts (now handled statically in theme.liquid)
     */
    checkPreconnectHints() {
      // Preconnect for fonts.gstatic.com is now handled statically in theme.liquid
      // This just verifies it exists
      const existing = document.querySelector('link[href*="fonts.gstatic.com"]');
      if (existing) {
        this.changes.push(`Verified preconnect hint exists: fonts.gstatic.com`);
      }
    }

    /**
     * Monitor font loading performance
     */
    monitorFontPerformance() {
      if ('fonts' in document) {
        const startTime = performance.now();
        
        document.fonts.ready.then(() => {
          const loadTime = performance.now() - startTime;
          console.log(`🔤 Font loading completed in ${Math.round(loadTime)}ms`);
          this.changes.push(`Font loading time: ${Math.round(loadTime)}ms`);
        });
      }
    }

    /**
     * Add safe CSS for better font rendering
     */
    addFontRenderingCSS() {
      // Only add if no existing font rendering CSS
      const existingStyle = document.querySelector('style[data-font-optimization]');
      if (!existingStyle) {
        const style = document.createElement('style');
        style.setAttribute('data-font-optimization', 'true');
        style.textContent = `
          /* Safe font rendering optimizations */
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          }
          
          /* Ensure text remains visible during webfont load */
          body {
            font-display: swap;
          }
        `;
        
        document.head.appendChild(style);
        this.changes.push('Added safe font rendering CSS');
      }
    }

    /**
     * Run minimal optimizations
     */
    optimize() {
      if (this.hasOptimized) return;
      
      console.log('🔤 Running minimal font optimizations...');
      
      try {
        // Wait a bit to ensure all fonts are loaded
        setTimeout(() => {
          if (CONFIG.addFontDisplay) {
            this.enhanceGoogleFontsUrls();
          }
          
          if (CONFIG.addPreconnectHints) {
            this.checkPreconnectHints();
          }
          
          if (CONFIG.monitorPerformance) {
            this.monitorFontPerformance();
          }
          
          this.addFontRenderingCSS();
          
          this.hasOptimized = true;
          
          if (this.changes.length > 0) {
            console.log('✅ Font optimizations applied:');
            this.changes.forEach(change => console.log('  •', change));
          } else {
            console.log('ℹ️ No font optimizations needed - already optimized');
          }
          
        }, 100);
        
      } catch (error) {
        console.error('❌ Error in font optimization:', error);
      }
    }

    /**
     * Get optimization status
     */
    getStatus() {
      return {
        hasOptimized: this.hasOptimized,
        changes: this.changes,
        mode: 'minimal'
      };
    }
  }

  // Initialize optimizer
  const fontOptimizer = new MinimalFontOptimizer();
  
  // Run when safe
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      fontOptimizer.optimize();
    });
  } else {
    fontOptimizer.optimize();
  }
  
  // Expose for debugging
  window.minimalFontOptimizer = fontOptimizer;
  
})();
